
import React, { useState, useEffect } from 'react'
import { get } from 'aws-amplify/api'
import { signOut } from 'aws-amplify/auth'
import { User, UserRole } from '../types/auth'
import { Employee } from '../types/workforce'
import { createApiHeaders, createImpersonatedApiHeaders, logApiCall } from './services/api-utils'
import { AuthContext } from './auth-context-definition'
import { useAmplify } from '../contexts/amplify-context'
import { secureLogger } from './secure-logger'
import { undoManager } from '@/services/undoManager'

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [originalRole, setOriginalRole] = useState<UserRole | null>(null)
  const { isConfigured: isAmplifyConfigured } = useAmplify()

  useEffect(() => {
    // Wait for Amplify to be configured before making API calls
    if (!isAmplifyConfigured) {
      return
    }

    const initializeAuth = async () => {
      try {
        // Check for user profile stored by AuthContainer
        const storedUserProfile = localStorage.getItem('user-profile')

        if (storedUserProfile) {
          const userProfile: User = JSON.parse(storedUserProfile)

          // Check if user object has originalRole (from previous session)
          if (userProfile.originalRole) {
            setOriginalRole(userProfile.originalRole)
          }

          // Check if there's a role override in localStorage
          const storedRoleOverride = localStorage.getItem('role-override')
          if (storedRoleOverride) {
            // Role override exists
            if (!userProfile.originalRole) {
              // First time seeing this override - store current role as original
              userProfile.originalRole = userProfile.role
              setOriginalRole(userProfile.role)
            }
            // Apply the override
            userProfile.role = storedRoleOverride as UserRole
          }

          // Use stored profile temporarily to show UI quickly
          setUser(userProfile)

          // ALWAYS fetch full profile from backend to get employee data and latest info
          try {
            const refreshedProfile = await getUserProfile()
            if (refreshedProfile) {
              setUser(refreshedProfile)
              localStorage.setItem('user-profile', JSON.stringify(refreshedProfile))
            }
          } catch (refreshError) {
            // Failed to refresh - using cached version
          }
        }
      } catch (error) {
        secureLogger.error('Failed to restore user profile session', { error: error instanceof Error ? error.message : 'Unknown error' })
        // Clear invalid session data
        localStorage.removeItem('user-profile')
      } finally {
        setIsLoading(false)
      }
    }

    initializeAuth()
  }, [isAmplifyConfigured])

  // Get user profile from backend API using AWS Amplify
  // This is the SINGLE SOURCE OF TRUTH for user data
  // Fetches from /users/me AND /workforce/employees/me, then merges
  const getUserProfile = async (): Promise<User | null> => {
    try {
      logApiCall('/users/me')

      // Check the exact config structure
      const { Amplify } = await import('aws-amplify')
      const config = Amplify.getConfig()

      // Fetch user from /users/me (generic user module)
      const { response: userResponse } = await get({
        apiName: 'users',
        path: '/users/me',
        options: {
          headers: createApiHeaders()
        }
      })

      const userResult = await userResponse
      const apiData = await userResult.body.json() as Record<string, unknown>
    // Parse basic user data
    const userData: User = {
      id: apiData.id as string || apiData.sub as string || 'unknown',
      email: apiData.email as string,
      role: apiData.roleName as UserRole,
      name: apiData.name as string || apiData.email as string,
      status: apiData.status as 'active' | 'inactive' | 'pending' | 'onboarding' || 'active',
      avatar: apiData.avatar as string | undefined,
      employee: undefined // Will be populated below
    }
    // Try to fetch employee data (not all users are employees)
    try {
      logApiCall('/workforce/employees/me')

      const { response: employeeResponse } = await get({
        apiName: 'workforce',
        path: '/workforce/employees/me',
        options: {
          headers: createImpersonatedApiHeaders()
        }
      })

      const employeeResult = await employeeResponse
      const employeeData = await employeeResult.body.json()

      // Validate and attach employee data to user
      if (employeeData && typeof employeeData === 'object' && 'employeeId' in employeeData) {
        userData.employee = employeeData as unknown as Employee
      }
    } catch (employeeError) {
      // User is not an employee (admin, contractor, etc.)
      userData.employee = undefined
    }

    return userData

    } catch (error) {
      secureLogger.error('Failed to get user profile from /users/me', { error: error instanceof Error ? error.message : 'Unknown error' })
      return null
    }
  }

  const logout = async () => {
    try {
      // First, sign out from AWS Cognito
      await signOut()

      // Clear undo manager stack on logout
      if (undoManager.isInitialized()) {
        undoManager.reset()
      }

      // Then clear local state and storage
      setUser(null)
      localStorage.removeItem('user-profile')
      localStorage.removeItem('tenant-override') // Clean up tenant data

      // Redirect to login
      window.location.href = '/login'

    } catch (error) {
      secureLogger.error('Error during logout', { error: error instanceof Error ? error.message : 'Unknown error' })

      // Clear undo manager even on error
      if (undoManager.isInitialized()) {
        undoManager.reset()
      }

      // Even if AWS signOut fails, clean up local data and redirect
      setUser(null)
      localStorage.removeItem('user-profile')
      localStorage.removeItem('tenant-override')
      window.location.href = '/login'
    }
  }

  // Refresh user profile from API
  const refreshUserProfile = async (): Promise<User | null> => {
    const profile = await getUserProfile()
    if (profile) {
      setUser(profile)
      localStorage.setItem('user-profile', JSON.stringify(profile))
    }
    return profile
  }

  // Immediate sync with localStorage (no delays, no clearing)
  const syncWithStorage = () => {
    try {
      const storedUserProfile = localStorage.getItem('user-profile')
      if (storedUserProfile) {
        const userProfile: User = JSON.parse(storedUserProfile)
        setUser(userProfile)
        return true
      } else {
        setUser(null)
        return false
      }
    } catch (error) {
      secureLogger.error('Error syncing with storage', { error: error instanceof Error ? error.message : 'Unknown error' })
      setUser(null)
      return false
    }
  }

  // Force refresh auth state (useful after authentication issues)
  const refreshAuth = async () => {
    setIsLoading(true)
    try {

      // Don't clear localStorage - that defeats the purpose!
      // Just check current state multiple times to handle race conditions
      let attempts = 0
      const maxAttempts = 5
      let userProfile: User | null = null

      while (attempts < maxAttempts && !userProfile) {
        attempts++

        // Check for user profile
        const storedUserProfile = localStorage.getItem('user-profile')
        if (storedUserProfile) {
          try {
            userProfile = JSON.parse(storedUserProfile)
            break
          } catch (parseError) {
            secureLogger.warn('Failed to parse user profile, removing', { error: parseError instanceof Error ? parseError.message : 'Unknown parse error' })
            localStorage.removeItem('user-profile')
          }
        }

        // Wait between attempts, with increasing delay
        if (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, attempts * 100))
        }
      }

      if (userProfile) {
        setUser(userProfile)
      } else {
        setUser(null)
      }
    } catch (error) {
      secureLogger.error('Error during auth refresh', { error: error instanceof Error ? error.message : 'Unknown error' })
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  // Override user role (admin only feature for testing)
  const overrideUserRole = (role: UserRole) => {
    if (!user) return
    if (user.role !== 'admin' && !user.originalRole) {
      secureLogger.error('Only admins can override roles')
      return
    }

    // Store original role if first override
    if (!user.originalRole) {
      setOriginalRole(user.role)
    }

    // Update user with new role and originalRole
    const updatedUser = {
      ...user,
      role,
      originalRole: user.originalRole || user.role
    }
    setUser(updatedUser)

    // Update localStorage
    localStorage.setItem('user-profile', JSON.stringify(updatedUser))
    localStorage.setItem('role-override', role)

  }

  // Clear role override
  const clearRoleOverride = () => {
    if (!user || !user.originalRole) return

    // Restore original role
    const updatedUser = {
      ...user,
      role: user.originalRole,
      originalRole: undefined
    }
    setUser(updatedUser)

    // Update localStorage
    localStorage.setItem('user-profile', JSON.stringify(updatedUser))
    localStorage.removeItem('role-override')
    setOriginalRole(null)

  }

  return (
    <AuthContext.Provider value={{
      user,
      logout,
      isLoading,
      refreshUser: refreshUserProfile,
      refreshAuth,
      syncWithStorage,
      overrideUserRole,
      clearRoleOverride,
      originalRole
    }}>
      {children}
    </AuthContext.Provider>
  )
}
